import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../services/config';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, AlertTriangle, Save } from 'lucide-react';

interface ThreatCategory {
  code: string;
  name: string;
  description?: string;
  color: string;
}

interface SubscriberSettings {
  email: string;
  alertThreshold: number;
  categories: string[];
  digestType: 'INSTANT' | 'DAILY' | 'WEEKLY' | 'PAUSED';
  language: 'en' | 'et' | 'ru' | 'ua';
  timezone: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  trialEndsAt?: string;
  unsubscribeToken: string;
}

export default function SettingsPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [settings, setSettings] = useState<SubscriberSettings | null>(null);
  const [availableCategories, setAvailableCategories] = useState<ThreatCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    
    Promise.all([
      fetch(`${API_BASE}/api/email/settings/${token}`).then(res => res.json()),
      fetch(`${API_BASE}/api/email/categories`).then(res => res.json()),
    ]).then(([settingsData, categoriesData]) => {
      if (!settingsData.error) {
        setSettings(settingsData);
      }
      setAvailableCategories(categoriesData);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    if (!settings || !token) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/email/settings/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alertThreshold: settings.alertThreshold,
          categories: settings.categories,
          digestType: settings.digestType,
          language: settings.language,
          timezone: settings.timezone,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: data.message || t('settings.saved') });
      } else {
        setMessage({ type: 'error', text: data.message || t('settings.error') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('settings.error') });
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!settings || !window.confirm(t('settings.unsubscribeConfirm'))) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/email/unsubscribe/${settings.unsubscribeToken}`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (data.success) {
        navigate('/unsubscribed');
      } else {
        setMessage({ type: 'error', text: data.message || t('settings.unsubscribeError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('settings.unsubscribeError') });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('settings.invalidToken')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('settings.invalidTokenMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('settings.title')}
            </h1>
          </div>
          
          <div className="space-y-6">
            {/* Current Email */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('settings.email')}</p>
              <p className="font-medium text-gray-900 dark:text-white">{settings.email}</p>
            </div>

            {/* Trial Status */}
            {settings.subscriptionTier === 'TRIAL' && settings.trialEndsAt && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('settings.trialEnds', { 
                    date: new Date(settings.trialEndsAt).toLocaleDateString() 
                  })}
                </p>
              </div>
            )}

            {/* Alert Frequency */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('settings.frequency')}
              </label>
              <select 
                value={settings.digestType} 
                onChange={(e) => setSettings({...settings, digestType: e.target.value as any})}
                className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={saving}
              >
                <option value="INSTANT">{t('settings.instant')}</option>
                <option value="DAILY">{t('settings.daily')}</option>
                <option value="WEEKLY">{t('settings.weekly')}</option>
                <option value="PAUSED">{t('settings.paused')}</option>
              </select>
            </div>

            {/* Risk Threshold */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('settings.threshold', { value: settings.alertThreshold })}
              </label>
              <input
                type="range"
                min="1"
                max="10"
                step="0.5"
                value={settings.alertThreshold}
                onChange={(e) => setSettings({...settings, alertThreshold: parseFloat(e.target.value)})}
                className="w-full"
                disabled={saving}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>{t('settings.low')}</span>
                <span>{t('settings.medium')}</span>
                <span>{t('settings.high')}</span>
              </div>
            </div>

            {/* Threat Categories */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('settings.categories')}
              </label>
              <div className="space-y-2">
                {availableCategories.map((category) => (
                  <label 
                    key={category.code} 
                    className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={settings.categories.includes(category.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings({
                            ...settings,
                            categories: [...settings.categories, category.code],
                          });
                        } else {
                          setSettings({
                            ...settings,
                            categories: settings.categories.filter(c => c !== category.code),
                          });
                        }
                      }}
                      className="mt-1 rounded border-gray-300 dark:border-gray-600"
                      disabled={saving}
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {category.name}
                      </div>
                      {category.description && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {category.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('settings.language')}
              </label>
              <select 
                value={settings.language} 
                onChange={(e) => setSettings({...settings, language: e.target.value as any})}
                className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={saving}
              >
                <option value="en">English</option>
                <option value="et">Eesti</option>
                <option value="ru">Русский</option>
                <option value="ua">Українська</option>
              </select>
            </div>

            {/* Status Messages */}
            {message && (
              <div className={`p-3 rounded-md text-sm ${
                message.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
              }`}>
                {message.text}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <button 
                onClick={handleSave}
                disabled={saving || settings.categories.length === 0}
                className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} />
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
              <button 
                onClick={handleUnsubscribe}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
              >
                {t('settings.unsubscribe')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
